import capitalize from "lodash/capitalize";
import { ProductJsonLd, ProductJsonLdProps } from "next-seo";
import { useRouter } from "next/router";

import { ProductPageProps } from "app/smartphone/[slug]/[reference]/page";
import {
  getProductDescription,
  getProductMinimumPrice,
} from "shared/product/ProductPageSeo/product-seo.helper";
import { extractStorageAndColorSlug } from "shared/product/ProductSection/product-section.helper";
import { applyCDNToUrl } from "ui/StrapiImage";
import { fromCents } from "utils/price.utils";

type ProductPageSeoProps = Pick<
  ProductPageProps,
  "productData" | "productAverageRating"
>;

export const ProductPageSeo = ({
  productData,
  productAverageRating: { count, avg },
}: ProductPageSeoProps) => {
  const { query } = useRouter();
  const referenceSlug = query.reference as string;

  const {
    family: { modelName, brand },
    variants,
  } = productData;

  const currentVariant =
    variants?.find(({ variantSlug }) => variantSlug === referenceSlug) ??
    variants?.[0];

  const [currentColorSlug, currentStorageSlug] =
    extractStorageAndColorSlug(referenceSlug);

  const productName = referenceSlug
    ? `${modelName} ${currentStorageSlug} ${currentColorSlug}`
    : `${modelName}`;

  const description = getProductDescription(productData);
  const minimumPrice = getProductMinimumPrice(productData);

  const images = [
    currentVariant?.combinedPicture?.medium?.url,
    currentVariant?.backPicture?.medium?.url,
    currentVariant?.frontPicture?.medium?.url,
  ]
    .map((url) => applyCDNToUrl(url))
    .filter(Boolean) as string[];

  const productConfig: ProductJsonLdProps = {
    productName,
    images,
    description,
    category: "smartphone",
    color: currentColorSlug,
    brand: capitalize(brand ?? ""),
    sku: currentVariant?.sku ?? "",
    offers: [
      {
        price: fromCents(minimumPrice).toString(),
        priceCurrency: "EUR",
        availability: "http://schema.org/InStock",
        itemCondition: "http://schema.org/UsedCondition",
        seller: {
          name: "MobileClub",
        },
      },
    ],
    aggregateRating: {
      ratingValue: avg,
      reviewCount: count.toString(),
    },
  };

  return <ProductJsonLd {...productConfig} />;
};
